/** SUNTRONIX ONE — Customer 360 identity-at-a-glance header (§66-67).
 *  Gap Closure §8 — full customer snapshot with warehouse, reuse,
 *  PO, address-verification, open issues, critical rules chip strip. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import AddressVerifiedBadge from "@/components/app/AddressVerifiedBadge";
import { up } from "@/components/identity/Identity";
import { CheckCircle2, AlertTriangle, Globe, Building2, DollarSign, Clock, Landmark, Apple, ShieldCheck, Warehouse, Recycle, ArrowDownUp, ClipboardCheck, PackageCheck, Zap } from "lucide-react";

export default function Customer360Header({ masterClientId, compact = false, testId }) {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const load = useCallback(async () => {
    if (!masterClientId) return;
    try {
      const { data } = await api.get(`/master-clients/${masterClientId}/header`);
      setData(data);
    } catch { /* ignore */ }
  }, [masterClientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="st-panel p-3 text-[12px] text-slate-500" data-testid={testId || "customer-header-loading"}>Loading customer…</div>;
  const score = data.completeness?.score ?? 0;
  const health = score >= 95 ? "ok" : score >= 60 ? "warn" : "risk";
  const healthCls = health === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : health === "warn" ? "bg-amber-50 text-amber-800 border-amber-200"
                    : "bg-red-50 text-red-800 border-red-200";

  const localTime = (() => {
    if (!data.time_zone) return "—";
    try {
      return now.toLocaleTimeString("en-GB", { timeZone: data.time_zone, hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  })();

  return (
    <div className="st-panel p-4" data-testid={testId || "customer-360-header"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            {data.master_client_name ? `Master client · ${data.master_client_name}` : "Customer"}
          </div>
          <div className="text-xl sm:text-2xl font-heading font-bold uppercase text-slate-900 mt-0.5" data-testid="customer-header-name">
            {up(data.display_name || data.legal_name)}
          </div>
          <div className="text-[12px] text-slate-600 mt-0.5" data-testid="customer-header-legal">
            {data.legal_name} · Code {data.customer_code}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 text-[11px] rounded border ${healthCls}`} data-testid="customer-header-completeness">
            Profile {score}%
          </span>
          <span className={`px-2 py-0.5 text-[11px] rounded border ${data.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
            {data.status}
          </span>
          {data.issues_open > 0 && (
            <span className="px-2 py-0.5 text-[11px] rounded border bg-red-50 text-red-800 border-red-200" data-testid="customer-header-issues">
              {data.issues_open} issue{data.issues_open === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      {!compact && (
        <>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            <HeaderChip icon={Globe} label="Country" value={data.country || "—"} testId="hdr-country" />
            <HeaderChip icon={DollarSign} label="Currency" value={data.base_currency || "—"} testId="hdr-currency" />
            <HeaderChip icon={Clock} label="Local time" value={localTime} testId="hdr-local-time"
              extra={<span className="ml-1 text-slate-400 text-[10px]">{data.time_zone || ""}</span>} />
            <HeaderChip icon={Landmark} label="Tax status" value={(data.tax_status || "unknown").replace(/_/g, " ")}
              extra={data.tax_identifier && (
                <span className="ml-1 text-slate-500">
                  {data.tax_identifier.type}: {data.tax_identifier.identifier?.slice(0, 6)}…
                  {data.tax_identifier.verified && <CheckCircle2 className="inline h-3 w-3 ml-1 text-emerald-600" strokeWidth={2} />}
                </span>
              )}
              testId="hdr-tax" />
            <HeaderChip icon={Apple} label="ABM"
              value={data.abm_used === "yes" ? (data.abm_id || "MISSING") : (data.abm_used || "—")}
              tone={data.abm_used === "yes" && !data.abm_id ? "warn" : "ok"} testId="hdr-abm" />
            <HeaderChip icon={ShieldCheck} label="MDM" value={data.mdm || "—"} testId="hdr-mdm" />
            <HeaderChip icon={Zap} label="AppleCare+" value={data.applecare_required ? "Required" : "Optional"} testId="hdr-applecare" />
            <HeaderChip icon={ArrowDownUp} label="Autopilot" value={data.autopilot_required ? "Required" : "No"} testId="hdr-autopilot" />
            <HeaderChip icon={Warehouse} label="Warehouse" value={data.warehouse || "—"} testId="hdr-warehouse" />
            <HeaderChip icon={Recycle} label="Reuse" value={data.reuse_min_grade ? `Grade ${data.reuse_min_grade}` : "—"} testId="hdr-reuse" />
            <HeaderChip icon={ClipboardCheck} label="Address verify"
              value={data.address_verification_required ? "Required" : "Optional"}
              tone={data.address_verification_required ? "ok" : undefined} testId="hdr-address-verify" />
            <HeaderChip icon={PackageCheck} label="PO"
              value={data.po_required ? "Required" : "Optional"} testId="hdr-po" />
            <HeaderChip icon={Building2} label="Registered"
              value={data.registered_address?.city ? `${data.registered_address.city}, ${data.registered_address.country}` : "—"}
              extra={data.registered_address && (
                <AddressVerifiedBadge size="sm"
                  status={data.registered_address.verified ? "SUNTRONIX_VERIFIED" : "NOT_VERIFIED"} />
              )}
              testId="hdr-registered" />
            <HeaderChip icon={Building2} label="Billing"
              value={data.billing_address?.city ? `${data.billing_address.city}, ${data.billing_address.country}` : "—"}
              testId="hdr-billing" />
          </div>

          {data.critical_rules?.length > 0 && (
            <div className="mt-4" data-testid="customer-header-critical-rules">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Customer critical rules</div>
              <div className="flex flex-wrap gap-1.5">
                {data.critical_rules.map((r, i) => (
                  <span key={i} className="px-2 py-0.5 text-[11px] rounded border bg-slate-900 text-white border-slate-900"
                    data-testid={`critical-rule-${i}`}>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}

          {data.never_rules?.length > 0 && (
            <div className="mt-3" data-testid="customer-header-never-rules">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Never rules</div>
              <div className="flex flex-wrap gap-1.5">
                {data.never_rules.map((r, i) => (
                  <span key={i} className="px-2 py-0.5 text-[11px] rounded border bg-red-50 text-red-800 border-red-200"
                    data-testid={`never-rule-${i}`}>
                    {r}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {data.completeness?.missing?.length > 0 && (
        <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2" data-testid="customer-header-missing">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" strokeWidth={1.6} />
          <div>
            <b>Profile gaps:</b> {data.completeness.missing.slice(0, 4).join(" · ")}
            {data.completeness.missing.length > 4 && ` · +${data.completeness.missing.length - 4} more`}
          </div>
        </div>
      )}
    </div>
  );
}

function HeaderChip({ icon: Icon, label, value, tone, extra, testId }) {
  const cls = tone === "warn" ? "text-amber-800" : "text-slate-800";
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
        <Icon className="h-3 w-3" strokeWidth={1.6} /> {label}
      </div>
      <div className={"mt-0.5 truncate " + cls}>
        {value}
        {extra}
      </div>
    </div>
  );
}
